import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { capitalize, integerName } from "../../app/utils";
import { AlertLevel } from "../../components/AlertMessage";
import { CustomSortIcon } from "../../components/DatatableHelpers";
import { CreatePeerReviewGroupButton } from "./CreatePeerReviewGroup";
import { RandomizePeerReviewGroupsButton } from "./RandomizePeerReviewGroups";
import { PeerReviewGroupRowActions } from "./PeerReviewGroupRowActions";
import { ManageReviewGroupRow, ManageReviewGroupsPageViewModel } from "./peerReviewGroupsApiSlice";

interface Props {
	data?: ManageReviewGroupsPageViewModel;
	setAlert: (message: string, level?: AlertLevel) => void;
}

export default function ManagePeerReviewGroups(props: Props) {
	const courseHasGroups = (props.data?.groupsPage.length ?? 0) > 0;
	const canCreateGroup = (props.data?.writerPool.length ?? 0) > 1;

	const statusMessage = () => {
		if (!props.data) return "";
		const groups = props.data.groupsPage.length;
		const pool = props.data.writerPool.length;
		if (groups == 0 && pool == 0) return "Course has no students.";
		if (pool == 0) return "All students are assigned to peer review groups.";
		if (groups == 0 && pool == 1) return "At least two students are needed for peer reviews.";
		if (groups == 0) return "No peer reviews groups have been created.";
		if (pool == 1) return "One student has not been assigned to a peer review group.";
		return `${capitalize(integerName(pool))} students have not been assigned to a peer review group.`;
	};

	const clearAlert = () => {
		props.setAlert("");
	};

	return (
		<div className="mt-8">
			{props.data && (
				<div className="flex justify-end">
					<CreatePeerReviewGroupButton
						onClick={clearAlert}
						makingCoursePromptGroup={false}
						disabled={!canCreateGroup}
					/>
					<RandomizePeerReviewGroupsButton
						hasGroups={courseHasGroups}
						makingCoursePromptGroups={false}
						disabled={!props.data.canRandomizeGroups}
						onClick={clearAlert}
					/>
				</div>
			)}
			<PeerReviewGroupsTable groups={props.data?.groupsPage} setAlert={props.setAlert} />
			<div className="flex justify-between items-start">
				<p className="text-sm mt-2 leading-6 text-gray-600 pr-3">
					NOTE: Do NOT change the groups until ALL students have requested peer reviews
					for their drafts.
				</p>
				<p className="mt-2 text-right">{statusMessage()}</p>
			</div>
		</div>
	);
}

interface PeerReviewGroupsTableProps {
	groups: ManageReviewGroupRow[] | undefined;
	setAlert: (message: string, level?: AlertLevel) => void;
}

function PeerReviewGroupsTable(props: PeerReviewGroupsTableProps) {
	const actionBodyTemplate = (row: ManageReviewGroupRow) => {
		return (
			<PeerReviewGroupRowActions
				rowId={row.id}
				rowName={row.name}
				setAlert={props.setAlert}
			/>
		);
	};

	return (
		<DataTable
			value={props.groups}
			dataKey="id"
			loading={props.groups == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ marginTop: "16px" }}>
			<Column header="Group Name" field="name" sortable></Column>
			<Column header="Students" field="studentNames" sortable></Column>
			<Column body={actionBodyTemplate} className="w-8 !pl-1 !pr-2"></Column>
		</DataTable>
	);
}
